// src/services/DonationReminderService.ts
/**
 * ❤️ Donation Reminder Service
 * Manages periodic donation reminders with respect for user experience
 */

import { NotificationService } from './NotificationService';

const DONATION_REMINDER_KEY = 'donation_reminder_last_shown';
const DONATION_DISMISSED_KEY = 'donation_reminder_dismissed_count';
const DONATION_INTERVAL_DAYS = 7; // Show reminder every 7 days
const MAX_DISMISSALS = 3; // After 3 dismissals, show less frequently (every 14 days)

export class DonationReminderService {
  /**
   * Check if it's time to show a donation reminder
   */
  static shouldShowReminder(): boolean {
    const lastShown = localStorage.getItem(DONATION_REMINDER_KEY);
    const dismissCount = parseInt(localStorage.getItem(DONATION_DISMISSED_KEY) || '0');

    // If never shown, show it
    if (!lastShown) {
      return true;
    }

    const lastShownDate = new Date(lastShown);
    const now = new Date();
    const daysSinceLastShown = Math.floor((now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24));

    // Adjust interval based on dismissal count (respect user's wishes)
    const interval = dismissCount >= MAX_DISMISSALS ? 14 : DONATION_INTERVAL_DAYS;

    return daysSinceLastShown >= interval;
  }

  /**
   * Create a donation reminder notification
   */
  static async createReminderNotification(userId: string): Promise<void> {
    const messages = [
      {
        title: '☕ Un Caffè per TradingSpace?',
        message: 'TradingSpace è gratuito e lo sarà sempre. Un semplice caffè può fare la differenza per continuare lo sviluppo! 💙'
      },
      {
        title: '❤️ Supporta il Progetto',
        message: 'Stai trovando utile TradingSpace? Il tuo contributo ci aiuta a mantenerlo gratuito per tutti. Un piccolo gesto, un grande impatto!'
      },
      {
        title: '🚀 Aiutaci a Crescere',
        message: 'Grazie per usare TradingSpace! Se ti piace la piattaforma, considera di supportarci con una donazione. Ogni caffè conta!'
      }
    ];

    // Random message for variety
    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    await NotificationService.createNotification(
      userId,
      'info',
      randomMessage.title,
      randomMessage.message,
      '/settings?tab=donations',
      {
        type: 'donation_reminder',
        timestamp: new Date().toISOString()
      }
    );

    // Update last shown timestamp
    localStorage.setItem(DONATION_REMINDER_KEY, new Date().toISOString());
  }

  /**
   * Mark reminder as dismissed (user closed it)
   */
  static markAsDismissed(): void {
    const currentCount = parseInt(localStorage.getItem(DONATION_DISMISSED_KEY) || '0');
    localStorage.setItem(DONATION_DISMISSED_KEY, (currentCount + 1).toString());
    localStorage.setItem(DONATION_REMINDER_KEY, new Date().toISOString());
  }

  /**
   * User donated - reset dismissal count and set long interval
   */
  static markAsDonated(): void {
    localStorage.setItem(DONATION_DISMISSED_KEY, '0');
    // Show next reminder in 30 days for donors
    const nextReminder = new Date();
    nextReminder.setDate(nextReminder.getDate() + 30);
    localStorage.setItem(DONATION_REMINDER_KEY, nextReminder.toISOString());
  }

  /**
   * Get statistics about reminder engagement
   */
  static getReminderStats(): {
    lastShown: string | null;
    dismissCount: number;
    daysSinceLastShown: number | null;
  } {
    const lastShown = localStorage.getItem(DONATION_REMINDER_KEY);
    const dismissCount = parseInt(localStorage.getItem(DONATION_DISMISSED_KEY) || '0');

    let daysSinceLastShown: number | null = null;
    if (lastShown) {
      const lastShownDate = new Date(lastShown);
      const now = new Date();
      daysSinceLastShown = Math.floor((now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      lastShown,
      dismissCount,
      daysSinceLastShown
    };
  }

  /**
   * Initialize donation reminder check (call on app startup)
   */
  static async initializeReminderCheck(userId: string): Promise<void> {
    try {
      // Wait a bit after login to not be intrusive (30 seconds)
      setTimeout(async () => {
        if (this.shouldShowReminder()) {
          await this.createReminderNotification(userId);
        }
      }, 30000); // 30 seconds delay
    } catch (error) {
      console.error('Error initializing donation reminder:', error);
    }
  }
}
